import { XCircleIcon } from "@heroicons/react/24/outline";
import { clsx } from "@mantine/core";
import { FC } from "react";

interface CancelledOffer {
  offer_id?: string | null;
  id?: string | null;
  offer_from?: string | null;
  offer_drawer?: string | null;
  offer_cancel_reason?: string | null;
  brokerage?: string | null;
  insurersinsurer_id?: string | null;
  offer_detail?: {
    offer_detail_id?: string | null;
    offersoffer_id?: string | null;
    policy_number?: string | null;
    insured_by?: string | null;
    currency?: string | null;
    offer_comment?: string | null;
    offer_details?: string | null;
    period_of_insurance_from?: string | null;
    period_of_insurance_to?: string | null;
  } | null;
  insurer?: {
    insurer_id?: string | null;
    insurer_company_name?: string | null;
    insurer_company_email?: string | null;
  } | null;
}

interface CancelledOfferCardProps {
  offer: CancelledOffer;
}

const CancelledOfferCard: FC<CancelledOfferCardProps> = ({ offer }) => {
  const cardBaseClasses =
    "relative bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 ease-in-out w-full min-w-[320px] border";
  const cardHoverClasses = "hover:shadow-xl hover:border-gray-400";
  const cardCancelledClasses = "border-l-4 border-l-gray-500 border-gray-200";

  return (
    <div
      className={clsx(cardBaseClasses, cardHoverClasses, cardCancelledClasses)}
    >
      <div className="absolute top-3 right-3 z-10 flex flex-col items-end space-y-2">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm bg-gray-100 text-gray-700 border border-gray-300">
          <XCircleIcon className="w-3 h-3 mr-1" />
          Cancelled
        </span>
      </div>

      <div className="p-5">
        <div className="mb-4 pr-16">
          <h3
            className="text-lg font-semibold text-gray-800 truncate"
            title={offer.offer_detail?.insured_by || "N/A"}
          >
            {offer.offer_detail?.insured_by || "N/A"}
          </h3>
          <p className="text-md text-gray-600 font-bold truncate">
            {offer.insurer?.insurer_company_name || "N/A"}
          </p>
          <p className="text-sm text-gray-600 truncate">
            Policy: {offer.offer_detail?.policy_number || "N/A"}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Offer ID
            </p>
            <p className="font-medium text-gray-700 truncate">
              {offer.offer_id || offer.id || "N/A"}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Source
            </p>
            <p className="font-medium text-gray-700">
              {offer.offer_from || "N/A"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Currency
            </p>
            <p className="font-medium text-gray-700">
              {offer.offer_detail?.currency || "N/A"}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Policy Number
            </p>
            <p className="font-medium text-gray-700 truncate">
              {offer.offer_detail?.policy_number || "N/A"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Period From
            </p>
            <p className="font-medium text-gray-700">
              {offer.offer_detail?.period_of_insurance_from
                ? new Date(
                    offer.offer_detail.period_of_insurance_from
                  ).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : "N/A"}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Period To
            </p>
            <p className="font-medium text-gray-700">
              {offer.offer_detail?.period_of_insurance_to
                ? new Date(
                    offer.offer_detail.period_of_insurance_to
                  ).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : "N/A"}
            </p>
          </div>
        </div>

        {offer.offer_cancel_reason && (
          <div className="mt-4 bg-gray-50 p-3 rounded-md border border-gray-300">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-1">
              Cancellation Reason
            </p>
            <p className="text-sm text-gray-800">{offer.offer_cancel_reason}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CancelledOfferCard;
